import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingShell } from "@/components/marketing-shell";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  Upload,
  Users,
  BookmarkCheck,
  LayoutDashboard,
  Shield,
  Zap,
  GitBranch,
} from "lucide-react";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";

export const Route = createFileRoute("/features")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Features, Super Affiliate OS" },
      {
        name: "description",
        content:
          "Anomaly detection, smart imports, contact intelligence, swipe files, and multi-workspace team collaboration for affiliate marketers.",
      },
      { property: "og:title", content: "Features, Super Affiliate OS" },
      {
        property: "og:description",
        content: "Everything you need to run a serious affiliate operation.",
      },
    ],
  }),
  component: FeaturesPage,
});

const features = [
  {
    icon: AlertTriangle,
    title: "Anomaly Detection",
    desc: "Statistical models flag drops, surges, and pattern breaks across every program. Never miss a payout discrepancy or traffic shift again.",
  },
  {
    icon: Upload,
    title: "Smart CSV Imports",
    desc: "Drop reports from any affiliate network. Auto-detect schema, map columns, and reconcile against your contact graph in seconds.",
  },
  {
    icon: Users,
    title: "Contact Intelligence",
    desc: "Every affiliate, AM, and partner unified across networks. LTV, churn risk, recurring vs second-tier, all in one record.",
  },
  {
    icon: BookmarkCheck,
    title: "Swipe Files",
    desc: "Searchable library of winning creatives, emails, and landing pages. Tag by program, vertical, or angle.",
  },
  {
    icon: LayoutDashboard,
    title: "Command Center",
    desc: "A single dashboard that rolls up performance, anomalies, and team activity across all workspaces.",
  },
  {
    icon: GitBranch,
    title: "Multi-Workspace",
    desc: "Up to 15 workspaces per account. Isolate clients, brands, or verticals with their own templates and team access.",
  },
  {
    icon: Zap,
    title: "Lovable AI Insights",
    desc: "Plain-English explanations for anomalies, suggested actions, and proactive recommendations powered by Lovable AI.",
  },
  {
    icon: Shield,
    title: "Enterprise Security",
    desc: "Row-level security on every table, account-scoped data, audit logs, and granular role-based permissions.",
  },
];

function FeaturesPage() {
  return (
    <MarketingShell>
      <section className="mx-auto max-w-4xl px-6 py-20 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          Every tool an affiliate operation needs
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
          From the moment a CSV lands to the moment you brief your team, Super Affiliate OS keeps
          everything in one place.
        </p>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-6 pb-24 sm:grid-cols-2">
        {features.map((f) => (
          <div key={f.title} className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <f.icon className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-semibold">{f.title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
          </div>
        ))}
      </section>

      <section className="border-t border-border/60 bg-card/40">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Ready to get started?</h2>
          <p className="mt-3 text-muted-foreground">
            Start your 14-day free trial. No credit card required.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button size="lg" asChild>
              <Link to="/signup">Start free trial</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/pricing">View pricing</Link>
            </Button>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
